import {
    call, put, takeEvery, all, select,
} from 'redux-saga/effects';
import lockr from 'lockr';
import { fetchApi, fetchApiAuth, getAuthKey } from '../utils/api';

import {
    startSessionSuccess,
    startSessionFailure,

    endSessionSuccess,
    endSessionFailure,

    sendHeartbeatSuccess,
    sendHeartbeatFailure,

    trackPageViewSuccess,
    trackPageViewFailure,

    sendPageViewsBatchSuccess,
    sendPageViewsBatchFailure,

    trackEventSuccess,
    trackEventFailure,

    sendEventsBatchSuccess,
    sendEventsBatchFailure,

    clearPageViewQueue,
    clearEventQueue,
} from './analyticsActions';

const {
    START_SESSION_REQUEST,
    END_SESSION_REQUEST,
    SEND_HEARTBEAT_REQUEST,
    TRACK_PAGE_VIEW_REQUEST,
    SEND_PAGE_VIEWS_BATCH_REQUEST,
    TRACK_EVENT_REQUEST,
    SEND_EVENTS_BATCH_REQUEST,
} = require('./analyticsActions').constants;

const ANALYTICS_SESSION_KEY = 'analytics_session_id';

// Helper function to get device type
function getDeviceType() {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
}

// Helper function to make API calls with or without auth
function* apiCall(endpoint, method, body) {
    const hasAuth = !!getAuthKey();
    const fetchFn = hasAuth ? fetchApiAuth : fetchApi;

    return yield call(fetchFn, {
        url: endpoint,
        method,
        body,
    });
}

// Start session saga
function* startSession() {
    try {
        const payload = {
            platform: 'web',
            device_type: getDeviceType(),
            app_version: null,
        };

        const response = yield call(apiCall, '/analytics/session/start', 'POST', payload);

        if (response.data && response.data.success) {
            const sessionId = response.data.data.session_id;
            lockr.set(ANALYTICS_SESSION_KEY, {
                sessionId,
                startedAt: new Date().toISOString(),
            });
            yield put(startSessionSuccess(response.data));
        } else {
            yield put(startSessionFailure('Failed to start session'));
        }
    } catch (error) {
        console.error('Failed to start analytics session:', error);
        yield put(startSessionFailure(error.response ? error.response.data.message : error.message));
    }
}

// End session saga
function* endSession(action) {
    try {
        const sessionId = action.payload?.sessionId || (yield select(state => state.analytics.sessionId));

        if (!sessionId) {
            yield put(endSessionFailure('No session to end'));
            return;
        }

        yield call(apiCall, '/analytics/session/end', 'POST', {
            session_id: sessionId,
        });

        lockr.rm(ANALYTICS_SESSION_KEY);
        yield put(endSessionSuccess());
    } catch (error) {
        console.error('Failed to end analytics session:', error);
        yield put(endSessionFailure(error.response ? error.response.data.message : error.message));
    }
}

// Heartbeat saga
function* sendHeartbeat(action) {
    try {
        const sessionId = action.payload?.sessionId || (yield select(state => state.analytics.sessionId));

        if (!sessionId) {
            yield put(sendHeartbeatFailure('No session for heartbeat'));
            return;
        }

        yield call(apiCall, '/analytics/session/heartbeat', 'POST', {
            session_id: sessionId,
        });

        yield put(sendHeartbeatSuccess());
    } catch (error) {
        console.error('Heartbeat failed:', error);
        yield put(sendHeartbeatFailure(error.response ? error.response.data.message : error.message));
    }
}

// Track single page view saga
function* trackPageView(action) {
    try {
        const sessionId = yield select(state => state.analytics.sessionId);

        if (!sessionId) {
            yield put(trackPageViewFailure('No session for page view'));
            return;
        }

        const { pageCategory, pagePath, pageTitle, referrerPath, timeSpentSeconds, viewedAt } = action.payload;

        yield call(apiCall, '/analytics/page-view', 'POST', {
            session_id: sessionId,
            page_category: pageCategory,
            page_path: pagePath,
            page_title: pageTitle,
            referrer_path: referrerPath || null,
            time_spent_seconds: timeSpentSeconds || 0,
            viewed_at: viewedAt || new Date().toISOString(),
        });

        yield put(trackPageViewSuccess());
    } catch (error) {
        console.error('Failed to track page view:', error);
        yield put(trackPageViewFailure(error.response ? error.response.data.message : error.message));
    }
}

// Send page views batch saga
function* sendPageViewsBatch(action) {
    try {
        const sessionId = yield select(state => state.analytics.sessionId);
        const pageViewQueue = yield select(state => state.analytics.pageViewQueue);

        if (!sessionId) {
            yield put(sendPageViewsBatchFailure('No session for batch page views'));
            return;
        }

        const pageViews = action.payload?.pageViews || pageViewQueue.toJS();

        if (pageViews.length === 0) {
            yield put(sendPageViewsBatchSuccess());
            return;
        }

        yield call(apiCall, '/analytics/page-view/batch', 'POST', {
            session_id: sessionId,
            page_views: pageViews,
        });

        yield put(clearPageViewQueue());
        yield put(sendPageViewsBatchSuccess());
    } catch (error) {
        console.error('Failed to send page views batch:', error);
        yield put(sendPageViewsBatchFailure(error.response ? error.response.data.message : error.message));
    }
}

// Track single event saga
function* trackEvent(action) {
    try {
        const sessionId = yield select(state => state.analytics.sessionId);

        const { eventType, eventCategory, eventLabel, eventValue, eventData, occurredAt } = action.payload;

        yield call(apiCall, '/analytics/event', 'POST', {
            session_id: sessionId,
            event_type: eventType,
            event_category: eventCategory || null,
            event_label: eventLabel || null,
            event_value: eventValue || null,
            event_data: eventData || null,
            occurred_at: occurredAt || new Date().toISOString(),
        });

        yield put(trackEventSuccess());
    } catch (error) {
        console.error('Failed to track event:', error);
        yield put(trackEventFailure(error.response ? error.response.data.message : error.message));
    }
}

// Send events batch saga
function* sendEventsBatch(action) {
    try {
        const sessionId = yield select(state => state.analytics.sessionId);
        const eventQueue = yield select(state => state.analytics.eventQueue);

        const events = action.payload?.events || eventQueue.toJS();

        if (events.length === 0) {
            yield put(sendEventsBatchSuccess());
            return;
        }

        yield call(apiCall, '/analytics/event/batch', 'POST', {
            session_id: sessionId,
            events,
        });

        yield put(clearEventQueue());
        yield put(sendEventsBatchSuccess());
    } catch (error) {
        console.error('Failed to send events batch:', error);
        yield put(sendEventsBatchFailure(error.response ? error.response.data.message : error.message));
    }
}

/**
 * Watch actions
 */
export default function* analyticsSaga() {
    yield all([
        takeEvery(START_SESSION_REQUEST, startSession),
        takeEvery(END_SESSION_REQUEST, endSession),
        takeEvery(SEND_HEARTBEAT_REQUEST, sendHeartbeat),
        takeEvery(TRACK_PAGE_VIEW_REQUEST, trackPageView),
        takeEvery(SEND_PAGE_VIEWS_BATCH_REQUEST, sendPageViewsBatch),
        takeEvery(TRACK_EVENT_REQUEST, trackEvent),
        takeEvery(SEND_EVENTS_BATCH_REQUEST, sendEventsBatch),
    ]);
}
